import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { api, ApiError } from '../../api/client';
import { showToast } from '../Toaster';
import type {
  ChartAnnotationDto,
  CreateChartAnnotationRequest,
  ListMentionCandidatesResponse,
  MentionCandidateDto,
} from '../../api/types';
import './AnnotationCreator.css';

interface Props {
  plantId: string;
  /** Prefill: instant timestamp (ms since epoch) if the user clicked a point,
   *  or [start, end] range if they brush-selected. */
  seed: { instant: number } | { range: [number, number] };
  /** Optional leaf id — annotation gets attached to a specific series. */
  leafId?: string | null;
  onSaved: (annotation: ChartAnnotationDto) => void;
  onCancel: () => void;
}

/**
 * Popover-style creator that appears when the user brush-selects a range or
 * clicks a point on the chart. Body input supports `@` typeahead: typing `@`
 * pops a candidate list fetched from
 * `/api/plants/{id}/mention-candidates?q=...`. Selecting a candidate inserts
 * `@[displayName](uuid)` at the cursor.
 */
export function AnnotationCreator({ plantId, seed, leafId, onSaved, onCancel }: Props) {
  const kind: 'instant' | 'range' = 'instant' in seed ? 'instant' : 'range';
  const [atLocal, setAtLocal] = useState(() =>
    toLocalInput(new Date('instant' in seed ? seed.instant : seed.range[0])));
  const [untilLocal, setUntilLocal] = useState(() =>
    'range' in seed ? toLocalInput(new Date(seed.range[1])) : '');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Typeahead state — kept minimal, no debounce library.
  const [tokenStart, setTokenStart] = useState<number | null>(null); // index in body where '@' was typed
  const [tokenQuery, setTokenQuery] = useState('');
  const [candidates, setCandidates] = useState<MentionCandidateDto[]>([]);
  const [candidatesBusy, setCandidatesBusy] = useState(false);

  useEffect(() => {
    if (tokenStart === null) return;
    const controller = new AbortController();
    setCandidatesBusy(true);
    const timer = window.setTimeout(async () => {
      try {
        const q = tokenQuery ? `?q=${encodeURIComponent(tokenQuery)}` : '';
        const res = await api.get<ListMentionCandidatesResponse>(
          `/api/plants/${plantId}/mention-candidates${q}`,
          controller.signal,
        );
        if (!controller.signal.aborted) setCandidates(res.candidates);
      } catch (err) {
        if (!controller.signal.aborted) {
          // eslint-disable-next-line no-console
          console.warn('Mention candidate lookup failed', err);
        }
      } finally {
        if (!controller.signal.aborted) setCandidatesBusy(false);
      }
    }, 150);
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [plantId, tokenStart, tokenQuery]);

  const handleBodyChange = (e: Event) => {
    const el = e.currentTarget as HTMLTextAreaElement;
    const text = el.value;
    setBody(text);
    // Track the position of the last '@' before the caret; if there's a
    // whitespace or newline between that '@' and the caret, close the popup.
    const caret = el.selectionStart ?? text.length;
    const at = text.lastIndexOf('@', caret - 1);
    if (at === -1) { setTokenStart(null); setTokenQuery(''); return; }
    const between = text.slice(at + 1, caret);
    if (/[\s@\[]/.test(between)) { setTokenStart(null); setTokenQuery(''); return; }
    setTokenStart(at);
    setTokenQuery(between);
  };

  const insertMention = (c: MentionCandidateDto) => {
    if (tokenStart === null) return;
    const el = textareaRef.current;
    const caret = el?.selectionStart ?? body.length;
    const before = body.slice(0, tokenStart);
    const after = body.slice(caret);
    const inserted = `@[${c.displayName}](${c.userId}) `;
    const next = before + inserted + after;
    setBody(next);
    setTokenStart(null);
    setTokenQuery('');
    // Restore focus + caret just after the inserted mention.
    queueMicrotask(() => {
      if (el) {
        el.focus();
        const pos = before.length + inserted.length;
        el.setSelectionRange(pos, pos);
      }
    });
  };

  const canSave = useMemo(() => {
    if (!body.trim()) return false;
    if (kind === 'range' && !untilLocal) return false;
    return true;
  }, [body, kind, untilLocal]);

  const submit = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const req: CreateChartAnnotationRequest = {
        kind,
        atUtc: new Date(atLocal).toISOString(),
        untilUtc: kind === 'range' ? new Date(untilLocal).toISOString() : null,
        leafId: leafId ?? null,
        body,
      };
      const created = await api.post<ChartAnnotationDto>(
        `/api/plants/${plantId}/annotations`,
        req,
      );
      onSaved(created);
      showToast({ tone: 'success', title: 'Annotation gespeichert' });
    } catch (err) {
      const msg = err instanceof ApiError && err.body
        ? JSON.stringify(err.body)
        : err instanceof Error ? err.message : String(err);
      showToast({ tone: 'error', title: 'Speichern fehlgeschlagen', message: msg });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div class="hv-ann-creator" role="dialog" aria-label="Annotation erstellen">
      <div class="hv-ann-creator__header">Neue Annotation</div>
      <div class="hv-ann-creator__field">
        <label>{kind === 'range' ? 'Von' : 'Zeitpunkt'}</label>
        <input
          type="datetime-local"
          value={atLocal}
          step={1}
          onChange={(e) => setAtLocal((e.currentTarget as HTMLInputElement).value)}
        />
      </div>
      {kind === 'range' && (
        <div class="hv-ann-creator__field">
          <label>Bis</label>
          <input
            type="datetime-local"
            value={untilLocal}
            step={1}
            onChange={(e) => setUntilLocal((e.currentTarget as HTMLInputElement).value)}
          />
        </div>
      )}
      <div class="hv-ann-creator__field hv-ann-creator__field--textarea">
        <label>Kommentar</label>
        <textarea
          ref={textareaRef}
          rows={4}
          value={body}
          placeholder="Beobachtung, Kontext, offene Frage. Tipp @ für Erwähnungen…"
          onInput={handleBodyChange}
        />
        {tokenStart !== null && (
          <div class="hv-ann-creator__typeahead">
            {candidatesBusy && candidates.length === 0
              ? <div class="hv-ann-creator__typeahead-empty">Suche…</div>
              : candidates.length === 0
                ? <div class="hv-ann-creator__typeahead-empty">Keine Treffer</div>
                : candidates.map((c) => (
                    <button
                      type="button"
                      key={c.userId}
                      class="hv-ann-creator__typeahead-item"
                      onClick={() => insertMention(c)}
                    >
                      <span class="hv-ann-creator__typeahead-name">{c.displayName}</span>
                      {c.email && <span class="hv-ann-creator__typeahead-mail">{c.email}</span>}
                    </button>
                  ))}
          </div>
        )}
      </div>
      <div class="hv-ann-creator__actions">
        <button type="button" class="hv-btn hv-btn--ghost" onClick={onCancel} disabled={saving}>
          Abbrechen
        </button>
        <button
          type="button"
          class="hv-btn hv-btn--primary"
          onClick={submit}
          disabled={!canSave || saving}
        >
          {saving ? 'Speichere…' : 'Speichern'}
        </button>
      </div>
    </div>
  );
}

function toLocalInput(d: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}
